import { RoleEnum } from '@acme/api';

export interface UserResponse {
  id?: string;
  email?: string;
  [key: string]: any;
}

export interface OrganizationResponse {
  id?: string;
  name?: string | null;
  inn?: string | null;
  ogrn?: string | null;
  legalAddress?: string | null;
  actualAddress?: string | null;
  phoneNumber?: string | null;
  responsiblePersonName?: string | null;
  role?: { id: RoleEnum; name: string };
  [key: string]: any;
}

export interface UserService {
  getUser: () => Promise<{ data: UserResponse | null }>;
  getOrganization?: () => Promise<{ data: OrganizationResponse | null }>;
  setToken?: (token: string) => void;
  clearToken?: () => void;
}

export interface AuthTokenResponse {
  accessToken?: string | null;
  [key: string]: any;
}

export class UserStore {
  userService: UserService;

  user: UserResponse | null = null;
  organization: OrganizationResponse | null = null;
  isLoading: boolean = false;
  tokenInstaled: boolean = false;
  private fetchPromise: Promise<void> | null = null;

  constructor(userService: UserService) {
    this.userService = userService;
  }

  async fetchUser() {
    if (this.isLoading && this.fetchPromise) {
      return this.fetchPromise;
    }

    this.isLoading = true;
    this.fetchPromise = (async () => {
      try {
        const userData = await this.userService.getUser();
        this.user = userData.data;

        if (this.userService.getOrganization) {
          try {
            const organizationData = await this.userService.getOrganization();
            this.organization = organizationData.data;
          } catch {
            this.organization = null;
          }
        }
      } catch {
        this.user = null;
        this.organization = null;
      } finally {
        this.isLoading = false;
        this.fetchPromise = null;
      }
    })();

    return this.fetchPromise;
  }

  setToken() {
    const tokenString = localStorage.getItem('token');
    const tokenResponse: AuthTokenResponse | null = tokenString
      ? JSON.parse(tokenString)
      : null;

    if (tokenResponse?.accessToken) {
      this.userService.setToken?.(tokenResponse.accessToken);
    }

    this.tokenInstaled = true;
  }

  getUser(tokenResponse: AuthTokenResponse) {
    if (tokenResponse?.accessToken) {
      this.fetchUser();
    }
  }

  login(tokenResponse: AuthTokenResponse) {
    localStorage.setItem('token', JSON.stringify(tokenResponse));
    if (tokenResponse?.accessToken) {
      this.userService.setToken?.(tokenResponse.accessToken);
    }
    this.fetchUser();
  }

  logout() {
    localStorage.removeItem('token');
    this.userService.clearToken?.();
    this.user = null;
    this.organization = null;
  }

  getAuth() {
    const tokenResponse = localStorage.getItem('token');
    return !!tokenResponse;
  }
}
